// helpdesk-frontend/src/components/Sidebar.js

import React, { useState, useMemo, useRef, useCallback, useEffect, useContext } from "react";
import { NavLink, useLocation } from "react-router-dom";
import styles from "./Sidebar.module.css";
import { useAuth } from "../hooks/useAuth";
import { motion, AnimatePresence } from "framer-motion";
import { useUI } from "../hooks/useUI";
import { useBranding } from "../context/BrandingContext";
import { apiService } from "../api/apiService";
// [WYDAJNOŚĆ 2026-09-12] Widoczność modułów — jedno pobranie na sesję, wspólne z `AuthContext`
// i `UIContext`. Powód, zasady unieważniania i pomiar: nagłówek `context/AuthContext.js`.
import {
    pobierzWidocznoscModulow,
    subskrybujWidocznoscModulow,
    wersjaPamieciWidocznosciModulow,
} from "../context/AuthContext";
import { localizedPath } from "../hooks/useLocalizedNavigate";
import { czyPokazacPanelAdministracyjny } from "../constants/adminTilesConfig";
import { MY_DEPT_TABS, filtrujZakladkiWgZasiegu, zakladkiDlaDzialu } from "../config/myDeptTabs";
import useMojDzialZasieg from "../hooks/useMojDzialZasieg";
// [O2 2026-09-13] Wybrany dział w hubie „Mój Dział" — podzakładki w menu nie gubią go przy przejściu.
import { useZakresMojegoDzialu } from "../api/zakresMojegoDzialu";
import { useSidebarBadges } from "../hooks/useSidebarBadges";
import { NotificationContext } from "../context/NotificationContext";
import SocialLinks from "./SocialLinks/SocialLinks";
import UserAvatar from "./common/UserAvatar/UserAvatar";

import { remoteWorkReportService } from "../services/remoteWorkReportService";

import {
    FiGrid,
    FiLogOut,
    FiChevronDown,
    FiChevronsLeft,
    FiChevronsRight,
    FiX,
    FiCalendar,
    FiBook,
    FiPlusSquare,
    FiFileText,
    FiLayers,
    FiUserPlus,
    FiHelpCircle,
    FiBriefcase,
    FiRss,
    FiShield,
    FiImage,
    FiAward,
    FiBarChart2,
    FiExternalLink,
    FiMail,
    FiCheckSquare,
    FiFolder, FiPenTool,
    FiTrello,
    FiBookOpen,
    FiClipboard,
    FiInbox,
    FiClock,
    FiSettings,
    FiUsers,
    // [PAKIET 2026-09-02] Ścieżka kariery (G), cele (H), oferty pracy (J).
    FiTrendingUp,
    FiTarget,
    // [poz.17 2026-09-04] Wejście „Moje zgłoszenia".
    FiMessageSquare,
    // [F-8 2026-09-11] Menedżer haseł w Narzędziach.
    FiLock,
    // [P1 2026-10-01] Grupa ZGODNOŚĆ: „Procedury i bezpieczeństwo” (segregator — ta sama ikona co kafelek /admin).
    FiArchive,
} from "react-icons/fi";

/* ═══ [P1 2026-10-01, „Cykl życia na pasku”] GRUPA „ZGODNOŚĆ” ═══
   Właściciel 1.10: „/wykaz-ksc — po co to jest (…) i dlaczego jest tutaj?”. Wykaz KSC stał w NARZĘDZIACH obok kalendarza
   i kanbana, a redakcja procedur nie miała w menu żadnej pozycji (wejście tylko przez /admin, którego osoba z samym kluczem
   bez rangi nie zawsze szuka). Teraz oba obowiązki prawne mają JEDNĄ małą grupę, widoczną WYŁĄCZNIE z kluczem —
   pracownik nie widzi nic nowego, a /polityki NIE wraca do menu (decyzja z 30.09: to jest wejście dla redakcji z kluczem,
   nie przywrócenie modułu dla wszystkich). Ukryty moduł gasi pozycję mimo klucza (reguła z 22.09). */
const KLUCZE_REDAKCJI_POLITYK = ["PolitykiRedakcja", "PolitykiUstawienia", "PolitykiRaportPotwierdzen", "PolitykiRejestrujDecyzje", "PolitykiRejestrujPotwierdzenia"];
const SLUG_PROCEDUR = "procedury-bezpieczenstwo";

/** Liczba przy pozycji menu (z liczników GET /polityki/moje-wejscia). Brak liczby albo 0 = nic — menu nie obiecuje pracy, której nie ma. */
const STYL_LICZNIKA = {
    flex: "0 0 auto",
    marginLeft: "0.375rem",
    minWidth: "1.125rem",
    padding: "0 0.375rem",
    borderRadius: "999px",
    fontSize: "0.625rem",
    fontWeight: 700,
    lineHeight: "1.125rem",
    textAlign: "center",
    fontVariantNumeric: "tabular-nums",
    background: "var(--sb-hover)",
    color: "var(--sb-text)",
};
const STYL_LICZNIKA_ZALEGLE = { ...STYL_LICZNIKA, background: "var(--color-danger-soft)", color: "var(--color-danger)" };
/** „1 procedura czeka”, „3 procedury czekają”, „5 procedur czeka” — po polsku, nie „3 procedur”. */
const odmienProcedury = (n) => {
    if (n === 1) return "1 procedura czeka";
    const d = n % 10;
    const s = n % 100;
    return d >= 2 && d <= 4 && (s < 12 || s > 14) ? `${n} procedury czekają` : `${n} procedur czeka`;
};
const LicznikPozycji = ({ item }) => {
    const n = Number(item?.licznik) || 0;
    if (n <= 0) return null;
    const zalegle = Number(item?.zaleglych) || 0;
    const opis = item.opisLicznika ? item.opisLicznika(n, zalegle) : String(n);
    return (
        <span style={zalegle > 0 ? STYL_LICZNIKA_ZALEGLE : STYL_LICZNIKA} title={opis} aria-label={opis} data-testid="licznik-pozycji">
            {n}
        </span>
    );
};

const Tooltip = ({ children, label, show }) => {
    if (!show) return children;
    return (
        <div className={styles.tooltipWrapper}>
            {children}
            <div className={styles.tooltip}>{label}</div>
        </div>
    );
};

const NavItem = ({ item, collapsed, showDot }) => {
    /* [P1 2026-10-01] „Panel administracyjny” (/admin) nie świeci razem z „Procedury i bezpieczeństwo” (/admin/procedury-…) —
       dwa podświetlone paski naraz właściciel zgłaszał już 06.09 („ten pasek nie świeci mi się 2x na niebiesko”). */
    const location = useLocation();
    const aktywnoscWylaczona = Array.isArray(item.nieAktywnyNa)
        && item.nieAktywnyNa.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
    const handleClick = (e) => {
        if (item.action) {
            e.preventDefault();
            item.action();
        }
    };

    const icon = <span className={styles.navIcon}>{item.icon}</span>;
    const label = !collapsed && <span className={styles.navLabel}>{item.label}</span>;
    const ext = !collapsed && item.external && <FiExternalLink className={styles.externalIcon} />;
    const dot = showDot && <span className={styles.newDot} />;

    if (item.action) {
        return (
            <li>
                <Tooltip label={item.label} show={collapsed}>
                    <button
                        className={`${styles.navLink} ${item.className || ""} ${collapsed ? styles.navLinkCollapsed : ""}`}
                        onClick={handleClick}
                        type="button"
                    >
                        {icon}{label}{ext}{dot}
                    </button>
                </Tooltip>
            </li>
        );
    }

    return (
        <li>
            <Tooltip label={item.label} show={collapsed}>
                <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                        `${styles.navLink} ${isActive && !aktywnoscWylaczona ? styles.active : ""} ${item.className || ""} ${collapsed ? styles.navLinkCollapsed : ""}`
                    }
                >
                    {icon}{label}{!collapsed && <LicznikPozycji item={item} />}{ext}{dot}
                </NavLink>
            </Tooltip>
        </li>
    );
};

const DropdownItem = ({ item, collapsed, hasNewFn }) => {
    const location = useLocation();
    const isParentPathActive = item.children?.some(
        (child) => child.path && location.pathname.startsWith(child.path)
    );
    const [isOpen, setIsOpen] = useState(isParentPathActive);
    const [hoverOpen, setHoverOpen] = useState(false);
    const [popoutTop, setPopoutTop] = useState(0);
    const btnRef = useRef(null);
    const hoverTimeout = useRef(null);

    const handleMouseEnter = useCallback(() => {
        if (!collapsed) return;
        clearTimeout(hoverTimeout.current);
        if (btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            setPopoutTop(rect.top);
        }
        setHoverOpen(true);
    }, [collapsed]);

    const handleMouseLeave = useCallback(() => {
        hoverTimeout.current = setTimeout(() => setHoverOpen(false), 180);
    }, []);

    const handlePopoutEnter = useCallback(() => {
        clearTimeout(hoverTimeout.current);
    }, []);

    const submenuVariants = {
        hidden: { height: 0, opacity: 0 },
        visible: { height: "auto", opacity: 1, transition: { duration: 0.28, ease: [0.4, 0, 0.2, 1], when: "beforeChildren", staggerChildren: 0.03 } },
    };
    const childVariants = {
        hidden: { opacity: 0, x: -8 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.2 } },
    };

    // Check if any child has new content → show dot on parent
    const parentHasNew = hasNewFn && item.children?.some(child => child.path && hasNewFn(child.path));

    if (collapsed) {
        return (
            <li
                className={styles.popoutWrapper}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                ref={btnRef}
            >
                <Tooltip label={item.label} show={!hoverOpen}>
                    <button
                        className={`${styles.navLink} ${styles.navLinkCollapsed} ${isParentPathActive ? styles.active : ""}`}
                        type="button"
                    >
                        <span className={styles.navIcon}>{item.icon}</span>
                        {parentHasNew && <span className={styles.newDot} />}
                    </button>
                </Tooltip>
                <AnimatePresence>
                    {hoverOpen && (
                        <motion.div
                            className={styles.popoutPanel}
                            style={{ top: popoutTop }}
                            initial={{ opacity: 0, x: -8, scale: 0.96 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            exit={{ opacity: 0, x: -8, scale: 0.96 }}
                            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                            onMouseEnter={handlePopoutEnter}
                            onMouseLeave={handleMouseLeave}
                        >
                            <div className={styles.popoutTitle}>{item.label}</div>
                            {item.children.map((child, i) => (
                                <div key={child.path || i}>
                                    {child.action ? (
                                        <button onClick={child.action} className={styles.popoutItem} type="button">
                                            <span className={styles.popoutItemIcon}>{child.icon}</span>
                                            <span>{child.label}</span>
                                        </button>
                                    ) : (
                                        <NavLink
                                            to={child.search ? `${child.path}${child.search}` : child.path}
                                            className={({ isActive }) =>
                                                `${styles.popoutItem} ${isActive ? styles.popoutItemActive : ""}`
                                            }
                                        >
                                            <span className={styles.popoutItemIcon}>{child.icon}</span>
                                            <span>{child.label}</span>
                                            <LicznikPozycji item={child} />
                                            {hasNewFn && child.path && hasNewFn(child.path) && (
                                                <span className={styles.newDot} data-testid="kropka-podpozycji" />
                                            )}
                                        </NavLink>
                                    )}
                                </div>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>
            </li>
        );
    }

    return (
        <li>
            <button
                className={`${styles.navLink} ${styles.dropdownBtn} ${isParentPathActive ? styles.parentActive : ""}`}
                onClick={() => setIsOpen(!isOpen)}
                type="button"
            >
                <span className={styles.navIcon}>{item.icon}</span>
                <span className={styles.navLabel}>{item.label}</span>
                {parentHasNew && <span className={styles.newDot} />}
                <FiChevronDown className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`} />
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.ul
                        className={styles.submenu}
                        variants={submenuVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                    >
                        {item.children.map((child, i) => (
                            <motion.li key={child.path || i} variants={childVariants}>
                                {child.action ? (
                                    <button onClick={child.action} className={`${styles.navLink} ${styles.subLink}`} type="button">
                                        <span className={styles.subDot} />
                                        <span className={styles.navLabel}>{child.label}</span>
                                    </button>
                                ) : (
                                    <NavLink
                                        to={child.search ? `${child.path}${child.search}` : child.path}
                                        className={({ isActive }) =>
                                            `${styles.navLink} ${styles.subLink} ${isActive ? styles.active : ""}`
                                        }
                                    >
                                        <span className={styles.subDot} />
                                        <span className={styles.navLabel}>{child.label}</span>
                                        <LicznikPozycji item={child} />
                                        {/* [zgłoszenie właściciela 2026-09-05] Kropka przy PODPOZYCJI, której sprawa dotyczy —
                                            nie tylko przy rodzicu. Rodzic dalej świeci, gdy świeci którekolwiek dziecko. */}
                                        {hasNewFn && child.path && hasNewFn(child.path) && (
                                            <span className={styles.newDot} data-testid="kropka-podpozycji" />
                                        )}
                                    </NavLink>
                                )}
                            </motion.li>
                        ))}
                    </motion.ul>
                )}
            </AnimatePresence>
        </li>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
   [WYDAJNOŚĆ 2026-09-12] JURY KONKURSÓW — JEDNO PYTANIE NA SESJĘ, NIE NA KAŻDE WEJŚCIE.

   POMIAR (dziennik produkcyjny, 3 dni): `GET /api/contests/jury/my-contests` poszło 264 razy, choć
   odpowiada wyłącznie na pytanie „czy pokazać pozycję Panel Jury w menu". Efekt wisiał na tożsamości
   `user`, która przy każdym wejściu do aplikacji zmienia się dwukrotnie (`AuthContext.checkLoggedIn`),
   więc pytanie szło co najmniej dwa razy na wejście — dla wszystkich, także dla osób, które nigdy
   nie były w żadnym jury.

   Pamięć jest KLUCZOWANA KONTEM (inny człowiek przy tym samym komputerze pyta na nowo), gaśnie przy
   wylogowaniu (zdarzenie `sesja-zakonczona` z `AuthContext.logout`) oraz przy każdej zmianie konkursu
   na żywo (`contest-upserted` / `contest-removed` z `NotificationContext`) — czyli dokładnie wtedy,
   gdy odpowiedź mogła się zmienić. Zdarzenie zamiast importu, bo import `AuthContext` → `Sidebar`
   zamknąłby cykl.
   ═══════════════════════════════════════════════════════════════════════════════════════════════ */
let pamiecJury = null;   // { kontoId, aktywne }
let wTrakcieJury = null; // { kontoId, obietnica }
/* [PRZEGLAD ADWERSARIALNY 2026-09-12] Straz pokolenia. Bez niej zachodzil wyscig: `contest-upserted`
   przychodzi w trakcie lotu zapytania, kasuje pamiec, a chwile pozniej wraca STARA odpowiedz i osiada
   w pamieci na cala sesje karty — juror dopisany do wlasnie ruszajacego konkursu nie zobaczylby pozycji
   „Panel Jury” az do przeladowania strony. Kazde uniewaznienie podbija licznik; odpowiedz z poprzedniego
   pokolenia wraca do swojego konsumenta, ale NIE zostaje stanem sesji. */
let wersjaJury = 0;

const wyczyscPamiecJury = () => { pamiecJury = null; wTrakcieJury = null; wersjaJury += 1; };

if (typeof window !== "undefined") {
    window.addEventListener("sesja-zakonczona", wyczyscPamiecJury);
    window.addEventListener("contest-upserted", wyczyscPamiecJury);
    window.addEventListener("contest-removed", wyczyscPamiecJury);
}

/** Czy konto jest CZYNNYM jurorem (nabór/obrady albo wyniki sprzed najwyżej 3 dni). */
const czyCzynneJury = (contests) => {
    const now = new Date();
    const threeDaysMs = 3 * 24 * 60 * 60 * 1000;
    return (contests || []).some(c => {
        const phase = (c.phase || c.currentPhase || "").toLowerCase();
        if (phase.includes("nabór") || phase.includes("obrady")) return true;
        if (phase.includes("zakończon") || phase.includes("zakonczony")) {
            const pubDate = c.resultsPublishDate ? new Date(c.resultsPublishDate) : null;
            if (pubDate && (now - pubDate) <= threeDaysMs) return true;
        }
        return false;
    });
};

/* [polecenie właściciela 2026-09-30] „to nie są widoki dla każdego” — podpozycje „Do potwierdzenia” / „Do decyzji” pokazuje się
   tylko osobom, których dotyczą (GET /polityki/moje-wejscia). Pamięć jak przy jury: klucz = konto, jedno żądanie na sesję karty
   (zapadka H1/G2 — landing nie może kosztować więcej niż sufit), gaśnie przy wylogowaniu i przy zdarzeniach modułu polityk
   (`polityka-obowiazek-upserted`, `polityka-upserted`), po których odpowiedź mogła się zmienić. Straż pokolenia jak wyżej. */
let pamiecWejscPolityk = null;   // { kontoId, wejscia }
let wTrakcieWejscPolityk = null; // { kontoId, obietnica }
let wersjaWejscPolityk = 0;
const wyczyscPamiecWejscPolityk = () => { pamiecWejscPolityk = null; wTrakcieWejscPolityk = null; wersjaWejscPolityk += 1; };
if (typeof window !== "undefined") {
    window.addEventListener("sesja-zakonczona", wyczyscPamiecWejscPolityk);
    window.addEventListener("polityka-obowiazek-upserted", wyczyscPamiecWejscPolityk);
    window.addEventListener("polityka-upserted", wyczyscPamiecWejscPolityk);
}
const pobierzWejsciaPolityk = (kontoId) => {
    if (pamiecWejscPolityk && pamiecWejscPolityk.kontoId === kontoId) return Promise.resolve(pamiecWejscPolityk.wejscia);
    if (wTrakcieWejscPolityk && wTrakcieWejscPolityk.kontoId === kontoId) return wTrakcieWejscPolityk.obietnica;
    const mojeWydanie = wersjaWejscPolityk;
    const obietnica = (typeof apiService.getPolitykiMojeWejscia === "function" ? apiService.getPolitykiMojeWejscia() : Promise.reject(new Error("brak")))
        .then((r) => {
            const wejscia = r?.data || {};
            if (mojeWydanie === wersjaWejscPolityk) pamiecWejscPolityk = { kontoId, wejscia };
            return wejscia;
        })
        .catch(() => ({}))
        .finally(() => { if (wTrakcieWejscPolityk && wTrakcieWejscPolityk.obietnica === obietnica) wTrakcieWejscPolityk = null; });
    wTrakcieWejscPolityk = { kontoId, obietnica };
    return obietnica;
};

const pobierzStatusJury = (kontoId) => {
    if (pamiecJury && pamiecJury.kontoId === kontoId) return Promise.resolve(pamiecJury.aktywne);
    if (wTrakcieJury && wTrakcieJury.kontoId === kontoId) return wTrakcieJury.obietnica;
    const mojeWydanie = wersjaJury;
    const obietnica = apiService.getJuryContests()
        .then((res) => {
            const aktywne = czyCzynneJury(res.data || []);
            // Zapisujemy TYLKO, jesli w miedzyczasie nikt nie uniewaznil pamieci (patrz straz pokolenia wyzej).
            if (mojeWydanie === wersjaJury) pamiecJury = { kontoId, aktywne };
            if (wTrakcieJury && wTrakcieJury.kontoId === kontoId) wTrakcieJury = null;
            return aktywne;
        })
        .catch((error) => {
            console.error("Błąd sprawdzania statusu jury:", error);
            // Awarii NIE zapamiętujemy — inaczej jedno mrugnięcie sieci zdejmowałoby jurorowi
            // pozycję z menu do końca sesji.
            if (wTrakcieJury && wTrakcieJury.kontoId === kontoId) wTrakcieJury = null;
            return false;
        });
    wTrakcieJury = { kontoId, obietnica };
    return obietnica;
};

const Sidebar = () => {
    const { user, logout, hasPermission, isModuleRestricted, isModuleGloballyHidden, isAdmin } = useAuth();
    // [2026-09-30] Wejścia „Do potwierdzenia” / „Do decyzji” — serwer mówi, czy dotyczą zalogowanego (null = jeszcze nie wiadomo → ukryte).
    const [wejsciaPolityk, setWejsciaPolityk] = useState(null);
    const [wydanieWejsc, setWydanieWejsc] = useState(0);
    useEffect(() => {
        const odswiez = () => setWydanieWejsc((w) => w + 1);
        window.addEventListener("polityka-obowiazek-upserted", odswiez);
        window.addEventListener("polityka-upserted", odswiez);
        return () => { window.removeEventListener("polityka-obowiazek-upserted", odswiez); window.removeEventListener("polityka-upserted", odswiez); };
    }, []);
    useEffect(() => {
        let aktualny = true;
        if (!user?.id) { setWejsciaPolityk(null); return undefined; }
        pobierzWejsciaPolityk(user.id).then((w) => { if (aktualny) setWejsciaPolityk(w); });
        return () => { aktualny = false; };
    }, [user?.id, wydanieWejsc]);
    const { isMobile, isSidebarOpen, isSidebarCollapsed, toggleSidebar, closeMobileSidebar, openNewTicketModal, isTicketCreateVisible } = useUI();
    const { logoUrl, appName } = useBranding();
    const [showJury, setShowJury] = useState(false);
    const [showRemoteReport, setShowRemoteReport] = useState(false);
    /* [zgłoszenie właściciela 2026-09-11] „w menu — w sekcji Narzędzia NIE WIDZĘ ikonki do
       prywatnego sejfu moich haseł, to błąd”.

       ROOT CAUSE — i nie jest nim ani brakująca ikona, ani brakująca pozycja. Pozycja stoi
       w `toolItems` z ikoną `FiLock` od 2026-09-11, a mimo to nie renderowała się NIKOMU.
       Widoczność modułu miała w aplikacji DWA lustra, które odpowiadały ODWROTNIE na to samo
       pytanie „czy moduł bez wiersza w ModuleVisibilities jest widoczny”:

         • `AuthContext.isModuleGloballyHidden` (linia ~53) buduje zbiór UKRYTYCH
           (`isVisible === false`) — brak wiersza znaczy WIDOCZNY, i tak zachowuje się `ProtectedRoute`;
         • ten komponent budował zbiór WIDOCZNYCH (`isVisible === true`) — brak wiersza znaczył UKRYTY.

       Na produkcji tabela `ModuleVisibilities` NIE MA wiersza o `ModuleCode = "sejf"` (mają go m.in.
       „calendar” i „tasks”), bo kodu nie było w `MenuModuleCatalog`, a `ModuleVisibilityController`
       nie ma POST — takiego kodu NIE DA SIĘ dodać z panelu. Skutek dokładnie taki, jak zgłoszony:
       adres `/narzedzia/sejf` działa (trasa pyta AuthContext), a pozycji w menu nie ma i nie ma też
       czym jej włączyć. Dotyczy to KAŻDEGO modułu dokładanego do menu, nie tylko sejfu.

       NAPRAWA jest u źródła: pasek pyta o to samo, co trasa — o zbiór UKRYTYCH. Awaria zapytania
       zostaje przy dotychczasowej decyzji (fail-closed, `awaria`) — to osobna, świadoma reguła
       i nie mieszamy jej z brakiem wiersza. Wiersz dla „sejf” dosypuje `MenuModuleCatalog`. */
    const [widocznoscModulow, setWidocznoscModulow] = useState(null);
    const { hasNew, onNotification } = useSidebarBadges();
    const notifCtx = useContext(NotificationContext);
    // [N-6] Zasięg „Mój Dział" dla dropdownu — pytamy tylko posiadacza modułu; awaria = pełne menu.
    const { maZasiegOperacyjny, trybZakladek } = useMojDzialZasieg(hasPermission("ModuleMojDzial"));
    const { dzialId: wybranyDzialMojegoDzialu } = useZakresMojegoDzialu();
    /* [O2, polecenie właściciela 2026-09-13] „widzę ludzi z podziałem i tylko zakładki które ci pisałem że mają być dodane".
       Gdy w hubie wybrano dział, menu pokazuje ZESTAW ZAKŁADEK TEGO DZIAŁU (reguła J-5/N-6 per dział, licząc ją tam, gdzie
       liczy ją hub) — inaczej dropdown prowadziłby na ekran „zakładka poza zasięgiem" w dziale, w którym tej roli nie ma.
       Osobne wywołanie hooka, bo wyrażenie wyżej jest zamrożone zapadką przelozonyDyrektora.j5. */
    const { dzialy: dzialyMojegoDzialu } = useMojDzialZasieg(hasPermission("ModuleMojDzial"));
    const zakladkiWybranegoDzialu = useMemo(() => {
        if (!wybranyDzialMojegoDzialu) return null;
        const dzial = (dzialyMojegoDzialu || []).find((d) => String(d?.id) === String(wybranyDzialMojegoDzialu));
        return dzial ? zakladkiDlaDzialu(dzial) : null;
    }, [dzialyMojegoDzialu, wybranyDzialMojegoDzialu]);

    // Zarejestruj callback badge w NotificationContext.
    // [2026-09-08] Rejestracja zwraca funkcję WYREJESTROWANIA — od kiedy konsumentów kropek jest więcej
    // niż jeden (menu boczne + pasek zakładek), zbiór musi się sprzątać przy odmontowaniu.
    useEffect(() => {
        if (!notifCtx?.registerBadgeCallback) return undefined;
        return notifCtx.registerBadgeCallback(onNotification);
    }, [notifCtx, onNotification]);

    // Pokolenie pamięci widoczności — unieważnienie (wylogowanie, zmiana uprawnień, zapis w panelu
    // administratora) podbija je i odpala efekt niżej.
    const [wersjaWidocznosci, setWersjaWidocznosci] = useState(wersjaPamieciWidocznosciModulow);
    useEffect(() => subskrybujWidocznoscModulow(setWersjaWidocznosci), []);

    // Fetch module visibility — z pamięci sesji (pierwszy z trzech konsumentów powłoki pyta serwer).
    useEffect(() => {
        if (!user) return undefined;
        let cancelled = false;
        pobierzWidocznoscModulow().then(({ wiersze, blad, powod }) => {
            if (cancelled) return;
            if (blad) {
                console.error("Błąd ładowania widoczności modułów:", powod);
                // Awaria ≠ brak wiersza. Przy awarii nie wiemy NIC, więc zostaje dotychczasowa
                // decyzja: pozycje modułowe znikają (pozycje bez `moduleCode` zostają).
                setWidocznoscModulow({ ukryte: new Set(), awaria: true });
                return;
            }
            setWidocznoscModulow({
                ukryte: new Set(wiersze.filter(m => m.isVisible === false).map(m => m.moduleCode)),
                awaria: false,
            });
        });
        return () => { cancelled = true; };
    }, [user, wersjaWidocznosci]);

    // Check if user is an active juror — jedno pytanie na konto i sesję (patrz `pobierzStatusJury`).
    useEffect(() => {
        if (!user) return undefined;
        let cancelled = false;
        pobierzStatusJury(user.id ?? null).then((aktywne) => {
            if (!cancelled) setShowJury(aktywne);
        });
        return () => { cancelled = true; };
    }, [user]);

    // Check if user has eligible remote work days (sidebar item visibility)
    useEffect(() => {
        if (!user?.id) return;
        let cancelled = false;
        const check = async () => {
            try {
                const show = await remoteWorkReportService.shouldShowPage(user.id);
                if (!cancelled) setShowRemoteReport(show);
            } catch {
                if (!cancelled) setShowRemoteReport(false);
            }
        };
        check();
        const interval = setInterval(check, 120000); // co 2 minuty
        return () => { cancelled = true; clearInterval(interval); };
    }, [user?.id]);

    const getRoleName = (type) => {
        switch (type) {
            case 3: return "Administrator";
            // [O-9, 2026-09-08] Ranga moderatora usunięta. Wartość zastana nie ma udawać
            // „Użytkownik" — to jest różnica między „nie wiem" a „wiem, że to nie tak".
            case 2: return "Ranga zniesiona";
            default: return "Użytkownik";
        }
    };

    const userInitial = user?.firstName ? user.firstName.charAt(0).toUpperCase() : "?";

    const sections = useMemo(() => {
        if (!user) return [];
        const result = [];

        // Widget „Nowe zgłoszenie" (BRIEF-0003) — CTA znika, gdy admin ukryje moduł 'ticketCreate'.
        // [poz.17 2026-09-04] „Moje zgłoszenia" WRACA DO MENU. Strona /moje-zgloszenia i jej trasa
        // istniały przez cały czas, ale wejście do nich było ZAKOMENTOWANE w tym pliku od 2026-02-20
        // (commit 6a0853ba „Add surveys, special calendars, HR & profile" — NIE któraś z ostatnich fal).
        // Skutek: pracownik mógł założyć zgłoszenie, a potem nie miał jak sprawdzić, co się z nim dzieje,
        // chyba że kliknął link z maila. Wejście jest BEZWARUNKOWE: podgląd WŁASNYCH spraw nie zależy
        // od tego, czy administrator ukrył widget zakładania (to dwie różne rzeczy).
        /* ══ [polecenie właściciela 2026-09-06, pkt 7] „MOJE ZGŁOSZENIA" SCHODZI POD „OFERTY PRACY" ═══
           „Jak jestem w moje zgłoszenia, to ten pasek nie świeci mi się 2x na niebiesko. Przenieś
            zakładkę moje zgłoszenia pod kafelek menu oferty pracy, niech będzie niżej i będzie ok."

           ROOT CAUSE dwóch niebieskich paskow — zmierzony, NIE jest nim prefiks tras ani wspólny
           kod modułu: „Nowe zgłoszenie" nie ma trasy w ogóle (to przycisk akcji), więc NavLink
           nigdy go nie zaznacza. On świeci ZAWSZE, bo jako CTA ma na stałe wypełnienie akcentem.
           Gdy stal bezposrednio nad „Moimi zgłoszeniami", aktywna pozycja dostawała DOKŁADNIE ten sam
           kolor (`--accent-tile` w obu regułach accent.css) — i wychodziły dwa identyczne paski
           jeden pod drugim, z których tylko dolny znaczy „jesteś tutaj".

           Sekcja ZGŁOSZENIA zostaje więc z samym przyciskiem akcji, a podgląd własnych spraw schodzi
           na koniec sekcji INFORMACJE — tam, gdzie stoją pozostałe rzeczy „do przeczytania".
           Drugie pół naprawy (żeby CTA nigdy nie udawało zaznaczenia) jest w Sidebar.module.css. */
        const ticketItems = isTicketCreateVisible
            ? [{ type: "link", action: () => openNewTicketModal(), icon: <FiPlusSquare />, label: "Nowe zgłoszenie", className: styles.ctaBtn }]
            : [];
        if (ticketItems.length > 0) {
            result.push({ title: "ZGŁOSZENIA", items: ticketItems });
        }

        /* Widoczny = NIEUKRYTY globalnie (ModuleVisibility to DENY-lista) ORAZ nieukryty dla tego
           usera (druga deny-lista). Brak wiersza = widoczny — tak samo jak w `ProtectedRoute`;
           powód rozjazdu opisany przy `widocznoscModulow` wyżej. `null` = jeszcze nie wczytano
           (fail-open na krótkim oknie startu), `awaria` = zapytanie padło (fail-closed). */
        const isModuleVisible = (code) => {
            if (isModuleRestricted(code)) return false;
            if (!widocznoscModulow) return true;
            if (widocznoscModulow.awaria) return false;
            return !widocznoscModulow.ukryte.has(code);
        };

        // Konto Office 365? (user.domain = bool isO365). „Moje pliki" to nakładka na OneDrive/SharePoint —
        // dla kont lokalnych zakładka się nie pojawia.
        const isO365 = user?.domain === true;

        const infoItems = [
            { type: "link", path: localizedPath("dashboard"), icon: <FiGrid />, label: "Dashboard", moduleCode: "dashboard" },
            { type: "link", path: localizedPath("notificationCenter"), icon: <FiInbox />, label: "Centrum komunikacji", moduleCode: "notificationCenter" },
            { type: "link", path: localizedPath("news"), icon: <FiRss />, label: "Aktualności", moduleCode: "news" },
            { type: "link", path: localizedPath("gallery"), icon: <FiImage />, label: "Galeria zdjęć", moduleCode: "gallery" },
            { type: "link", path: localizedPath("contests"), icon: <FiAward />, label: "Strefa konkursów", moduleCode: "contests" },
            { type: "link", path: localizedPath("eLearning"), icon: <FiBookOpen />, label: "E-Learning", moduleCode: "elearning" },
            { type: "link", path: localizedPath("surveys"), icon: <FiClipboard />, label: "Ankiety", moduleCode: "surveys" },
            { type: "link", path: localizedPath("addressBook"), icon: <FiBook />, label: "Książka adresowa", moduleCode: "addressBook" },
            { type: "link", path: localizedPath("knowledgeBase"), icon: <FiHelpCircle />, label: "Baza wiedzy", moduleCode: "knowledgeBase" },
            /* [polecenie właściciela 2026-09-29] „zrób mi to jako dropbutton menu list” — „Dokumenty”, „Do potwierdzenia” i „Do decyzji”
               stały jako trzy osobne pozycje jedna pod drugą. Teraz to JEDNA rozwijana pozycja „Dokumenty” z trzema podpozycjami.
               Każda podpozycja zachowuje WŁASNY kod widoczności (dokumenty firmowe = `documents`, obowiązki polityk = `polityki`),
               więc ukrycie jednego modułu gasi tylko jego podpozycję; bez żadnej widocznej podpozycji znika cała grupa, a przy
               jednej — zostaje zwykła pozycja (rozwijanie jednej rzeczy to zbędne kliknięcie). Filtr dzieci stoi niżej, przy grupie.
               Zarządzanie politykami: [P1 2026-10-01] grupa ZGODNOŚĆ → „Procedury i bezpieczeństwo” (tylko z kluczem) albo panel /admin →
               „Compliance i cyberbezpieczeństwo” → „Procedury i bezpieczeństwo” (kafelek `procedury-bezpieczenstwo` w adminTilesConfig.js). */
            {
                type: "dropdown",
                icon: <FiFileText />,
                label: "Dokumenty",
                children: [
                    { path: localizedPath("documents"), icon: <FiFileText size={16} />, label: "Dokumenty firmowe", moduleCode: "documents" },
                    /* [POLITYKI 2026-09-28, E7 §6/D31] „Do potwierdzenia” — obowiązek zapoznania z polityką; zieloną kropkę zapala
               powiadomienie polityka_do_potwierdzenia (useSidebarBadges). Pakiet POTWIERDZENIA może zawęzić wejście do osób
               z otwartym obowiązkiem.
               [POLITYKI-poprawka 2026-09-29] moduleCode "polityki" (WŁASNY moduł, nie „documents”): przegląd wykrył, że pozycja
               wisiała u każdego konta i nie dało się jej zgasić (moduł z PermissionCatalog bez wiersza ModuleVisibilities).
               Ukrycie modułu zdejmuje WEJŚCIE, nie obowiązek (A15-06) — powiadomienie z linkiem /do-potwierdzenia dalej dochodzi
               (poza sitem NotificationLinkScope, R14); ukrycie to świadoma decyzja administratora w /admin/access-control. */
                    /* [polecenie właściciela 2026-09-30] „to nie są widoki dla każdego”: „Do potwierdzenia” — obowiązek zapoznania albo dział,
                       którego dotyczy procedura; „Do decyzji” — czynny udział w zarządzie albo głos w rundzie. Serwer liczy (GET /polityki/moje-wejscia),
                       menu tylko filtruje; do czasu odpowiedzi pozycje są ukryte (lepiej chwilę bez wejścia niż wejście u każdego). */
                    /* [P1 2026-10-01] Liczba przy podpozycji = `liczniki` z TEJ SAMEJ odpowiedzi moje-wejscia (P2: doPotwierdzenia z zaległymi
                       w środku, zaleglych = z tego po terminie, doDecyzji). Warunek WIDOCZNOŚCI zostaje na flagach — liczba tylko opisuje;
                       starszy serwer bez pola `liczniki` = pozycja bez liczby, nic więcej. */
                    { path: localizedPath("doPotwierdzenia"), icon: <FiCheckSquare size={16} />, label: "Do potwierdzenia", moduleCode: "polityki", wejscie: "doPotwierdzenia", licznik: wejsciaPolityk?.liczniki?.doPotwierdzenia, zaleglych: wejsciaPolityk?.liczniki?.zaleglych, opisLicznika: (n, z) => `${n} do potwierdzenia${z > 0 ? `, w tym ${z} po terminie` : ""}` },
                    /* [POLITYKI ZATWIERDZANIE 2026-09-29, E7 D23] „Do decyzji” — rundy przeglądu/zarządu, w których zalogowany ma głos (prawo z rundy,
                       bez klucza; skład liczy serwer). Kropkę zapala polityka_do_zatwierdzenia (link /polityki/do-decyzji). Ten sam moduleCode. */
                    { path: localizedPath("politykiDoDecyzji"), icon: <FiCheckSquare size={16} />, label: "Do decyzji", moduleCode: "polityki", wejscie: "doDecyzji", licznik: wejsciaPolityk?.liczniki?.doDecyzji, opisLicznika: (n) => `${n} czeka na Twój głos` },
                ].filter((c) => isModuleVisible(c.moduleCode) && (!c.wejscie || !!wejsciaPolityk?.[c.wejscie])),
            },
            /* [POLITYKI 2026-09-28, E7 D26] Zarządzanie politykami — wejście dla posiadaczy któregokolwiek z kluczy modułu `polityki`.
               [polecenie właściciela 2026-09-29, druga wiadomość] „dla tych co mają dostęp imienny ma być dostęp z poziomu menu tak jak teraz,
               ale sekcja admina ma odpowiadać za zbiorczą listę procedur” — pozycja WRACA (była zdjęta godzinę wcześniej, gdy powstał kafelek
               /admin „Procedury i bezpieczeństwo”). Oba wejścia prowadzą do tego samego modułu; klucze są need-to-know (ranga nic nie otwiera). */
            /* [polecenie właściciela 2026-09-30] „/polityki (…) znika mi to z aplikacji (…) ma być w Mój Dział, dostępna dla wprowadzenia
               i przygotowania przez pracownika, odpowiedzialność spływa na dyrektora; jeżeli ktoś ma mieć uprawnienia ogólne dodawania,
               ma sekcję admina” — pozycja ZDJĘTA z menu głównego: pracownik i kierownik → Mój Dział → „Procedury”; zbiorcza lista →
               /admin → „Procedury i bezpieczeństwo”. Trasa /polityki zostaje (linki z powiadomień do właściciela), bez wejścia w menu. */
            // [PAKIET 2026-09-02] Oferty pracy — treść firmowa dla każdego pracownika (jak konkursy);
            // zasięg publikacji rozstrzyga backend, menu steruje wyłącznie widocznością modułu.
            { type: "link", path: localizedPath("jobOffers"), icon: <FiBriefcase />, label: "Oferty pracy", moduleCode: "jobOffers" },
            /* [polecenie właściciela 2026-09-06, pkt 7] „przenieś zakładkę moje zgłoszenia pod kafelek
               menu oferty pracy" — stąd miejsce tej pozycji na końcu sekcji INFORMACJE. */
            /* ⚠️ ODWRÓCONA DECYZJA [polecenie właściciela 2026-09-22]. Do 22.09 wejście było tu
               BEZWARUNKOWE, z uzasadnieniem „podgląd własnych spraw i widget zakładania to dwie różne rzeczy".
               Właściciel to odwołał wprost: „Mówiłem, że jak usuwasz «Widget: Nowe zgłoszenie» —
               ukrywasz z menu — «moje zgłoszenia» też mają być ukryte, to jest jednoznaczne albo zrób
               oddzielnie ukrywanie dla sekcji «moje zgłoszenia»". Wybrane pierwsze: jeden przełącznik
               gasi cały moduł zgłoszeń w menu pracownika, bez mnożenia kodów widoczności.
               To jest warstwa MENU — trasa /moje-zgloszenia i API dalej odpowiadają wg uprawnień (A15-06). */
            { type: "link", path: localizedPath("myTickets"), icon: <FiMessageSquare />, label: "Moje zgłoszenia", moduleCode: "ticketCreate" },
        ].filter(item => !item.moduleCode || isModuleVisible(item.moduleCode))
            // Grupa rozwijana bez widocznych podpozycji znika; z jedną — staje się zwykłą pozycją (ikona grupy, nazwa podpozycji).
            .flatMap((item) => {
                if (item.type !== "dropdown") return [item];
                if (item.children.length === 0) return [];
                if (item.children.length === 1) {
                    const [jedyna] = item.children;
                    return [{ type: "link", path: jedyna.path, icon: item.icon, label: jedyna.label, moduleCode: jedyna.moduleCode, licznik: jedyna.licznik, zaleglych: jedyna.zaleglych, opisLicznika: jedyna.opisLicznika }];
                }
                return [item];
            });
        if (infoItems.length > 0) result.push({ title: "INFORMACJE", items: infoItems });

        const toolItems = [
            { type: "link", path: localizedPath("calendar"), icon: <FiCalendar />, label: "Kalendarz", moduleCode: "calendar" },
            { type: "link", path: localizedPath("kanban"), icon: <FiLayers />, label: "Tablica kanban", moduleCode: "kanban" },
            // Listy projektowe (styl MS Lists) — dla KAŻDEGO pracownika; steruje TYLKO widoczność modułu (menu admina).
            { type: "link", path: localizedPath("projects"), icon: <FiTrello />, label: "Projekty", moduleCode: "projects" },
            // Teams scalony do "Moje pliki" (Moje grupy) — strona /teams pozostaje pod URL, ale ukryta w menu.
            // { type: "link", path: localizedPath("teams"), icon: <FiVideo />, label: "Teams" },
            { type: "link", path: localizedPath("tasks"), icon: <FiCheckSquare />, label: "Zadania", moduleCode: "tasks" },
            { type: "link", path: localizedPath("myFiles"), icon: <FiFolder />, label: "Moje pliki", moduleCode: "myFiles", requireO365: true },
            { type: "link", path: localizedPath("mySignature"), icon: <FiPenTool />, label: "Moja stopka e-mail", moduleCode: "emailSignature" },
            // [F-8 2026-09-11] Menedzer hasel — „prywatny dla kazdego pracownika w Narzedziach".
            { type: "link", path: localizedPath("sejf"), icon: <FiLock />, label: "Menedzer hasel", moduleCode: "sejf" },
            /* [P1 2026-10-01] „Wykaz KSC” WYSZEDŁ z NARZĘDZI do grupy ZGODNOŚĆ (niżej) — to obowiązek prawny, nie narzędzie pracy. */
        ].filter(item => item && (!item.moduleCode || isModuleVisible(item.moduleCode)) && (!item.requireO365 || isO365));
        if (toolItems.length > 0) result.push({ title: "NARZĘDZIA", items: toolItems });

        // --- SPRAWY PRACOWNICZE (wszyscy zalogowani — sekcja osobista) ---
        const hrPersonalItems = [
            { type: "link", path: localizedPath("hrRequests"), icon: <FiClipboard />, label: "Wnioski pracownicze", moduleCode: "hrRequests" },
            { type: "link", path: localizedPath("hrMyWorkTime"), icon: <FiClock />, label: "Mój czas pracy", moduleCode: "workTime" },
            { type: "link", path: localizedPath("hrMyDocuments"), icon: <FiFileText />, label: "Moje dokumenty HR", moduleCode: "hrDocuments" },
            // [PAKIET 2026-09-02] Własna ścieżka rozwoju (G) i własne cele (H) — dane pracownika o nim samym,
            // więc bez klucza; ukrycie modułu robi administrator (globalnie lub imiennie).
            { type: "link", path: localizedPath("hrMyCareerPath"), icon: <FiTrendingUp />, label: "Moja ścieżka kariery", moduleCode: "careerPath" },
            { type: "link", path: localizedPath("myGoals"), icon: <FiTarget />, label: "Moje cele", moduleCode: "goals" },
            showRemoteReport && { type: "link", path: localizedPath("remoteWorkReport"), icon: <FiBriefcase />, label: "Rozliczenie zdalnej", highlight: true },
        ].filter(item => item && (!item.moduleCode || isModuleVisible(item.moduleCode)));
        if (hrPersonalItems.length > 0) result.push({ title: "SPRAWY PRACOWNICZE", items: hrPersonalItems });

        // [domknięcie luk 2026-10-01] Czy ZGODNOŚĆ → „Procedury i bezpieczeństwo” jest w menu (ten sam warunek co pozycja niżej) —
        // od tego zależy, gdzie stoi liczba „na Twój krok”.
        const pozycjaProcedurWZgodnosci = !isMobile && KLUCZE_REDAKCJI_POLITYK.some((k) => hasPermission(k)) && isModuleVisible("polityki");

        // --- OBSŁUGA PRACOWNIKA (widoczne tylko gdy user ma uprawnienia) ---
        const obslugaItems = [
            /* [polecenie właściciela 2026-09-21] „brakuje kolejnej sekcji Mój dział — która też powinna
               zostać ukryta, być opcja". Do tej fali cała sekcja stała WYŁĄCZNIE na kluczu
               `ModuleMojDzial`, a ten jest nadawany AUTOMATYCZNIE z funkcji w strukturze — administrator
               nie miał więc czym zgasić samego wejścia (odebrany klucz wracał przy logowaniu).
               Teraz wejście ma globalny wyłącznik `mojDzial` (MenuModuleCatalog, sekcja „hr").
               ⚠️ To WIDOCZNOŚĆ, nie autoryzacja (A15-06): trasa /moj-dzial i API modułu odpowiadają
               dalej wg klucza — tak samo jak „E-Teczka", która też gaśnie tylko w menu. */
            hasPermission("ModuleMojDzial") && isModuleVisible("mojDzial") && {
                type: "dropdown",
                icon: <FiUsers />,
                label: "Mój Dział",
                // Wszystkie navtab modułu jako podzakładki — aktywny rodzic (startsWith) + aktywna podzakładka (NavLink).
                // [PAKIET 2026-09-02] Zakładka z polem `permission` (np. „Dokumenty" na kluczu DeptDocsView)
                // pokazuje się WYŁĄCZNIE posiadaczowi klucza. Bez tego filtru dropdown obiecywałby zakładkę,
                // na której kierownik zobaczyłby wyłącznie ekran odmowy — a menu nie może kłamać (lekcja N1).
                // Filtr stoi w DWÓCH miejscach: tutaj i w MyDeptHubPage (pasek sekcji) — oba czytają to samo pole.
                // [N-6] Przełożony bez własnego zespołu (tylko działy raportujące) widzi trzy zakładki — ten sam
                // filtr co w pasku hubu (config/myDeptTabs.js), z jednego źródła.
                children: filtrujZakladkiWgZasiegu(MY_DEPT_TABS, maZasiegOperacyjny, trybZakladek)
                    .filter((t) => !t.permission || hasPermission(t.permission))
                    // [O2] Wybrany dział zawęża zestaw do JEGO roli — ten sam filtr, co w pasku hubu.
                    .filter((t) => !zakladkiWybranegoDzialu || zakladkiWybranegoDzialu.includes(t.id))
                    .map((t) => ({
                        label: t.label,
                        path: localizedPath(t.route),
                        // [O2] `?dzial=` wybranego działu — osobnym polem, bo po `path` liczy się aktywna pozycja i kropka nowości.
                        search: wybranyDzialMojegoDzialu ? `?dzial=${encodeURIComponent(wybranyDzialMojegoDzialu)}` : "",
                        icon: <t.icon size={16} />,
                        /* [domknięcie luk 2026-10-01, krytyk: „kierownik i właściciel bez klucza nie widzą w menu, że procedura czeka na ich krok”]
                           Liczba „moje do działania” (serwer liczy ją także dla kierowanych działów i właściciela) stała tylko przy ZGODNOŚĆ →
                           „Procedury i bezpieczeństwo”, której bez klucza nie ma. Gdy tej pozycji nie ma — ta sama liczba (to samo pole
                           moje-wejscia, bez nowego żądania) stoi przy Mój Dział → „Procedury”. Z kluczem liczba zostaje w ZGODNOŚCI (bez dubla). */
                        ...(t.route === "myDeptProcedury" && !pozycjaProcedurWZgodnosci
                            ? { licznik: wejsciaPolityk?.liczniki?.mojeDoDzialania, opisLicznika: (n) => `${odmienProcedury(n)} na Twój krok` }
                            : {}),
                    })),
            },
            /* ═══ [polecenie właściciela 2026-09-22 wieczór] STRUKTURA ORGANIZACYJNA — NIE MA JEJ TUTAJ ═══
               „sekcje /struktura-organizacyjna przenieś do /admin/struktura-organizacyjna — zrób dla tego
                kafelek i ma to być DOSTĘPNE W SEKCJI ADMIN (…) to uprawnienia administracyjne mają być,
                jest zła struktura linku."

               Pozycja stała tu przez jeden dzień (rano 2026-09-22, po pytaniu „w widoczności menu jest,
               a w menu nie ma") — i zeszła tego samego dnia, bo właściciel przeniósł CAŁY EDYTOR do
               administracji. Wejścia są teraz trzy i wszystkie w jednym miejscu, gdzie człowiek szuka
               narzędzi administracyjnych: kafelek „Struktura organizacyjna" na /admin (kategoria
               „Kadry i organizacja"), ten sam kafelek w sekcji administracyjnej landingu kadr
               (/obsluga-hr) i wyszukiwarka globalna, która podpowiada kafelki panelu.

               ⚠️ DWIE RZECZY, KTÓRYCH TO NIE ZABIERA PRACOWNIKOWI:
                 • OGLĄDANIE schematu — zakładka „Struktura" w /ksiazka-adresowa (StructureView.js)
                   rysuje go każdemu zalogowanemu i klucza administracyjnego nie wymaga;
                 • stary adres /struktura-organizacyjna — żyje jako przekierowanie (App.js), więc linki
                   z zakładek i z maili Zarządu dalej trafiają tam, gdzie trzeba.
               Dopisanie pozycji z powrotem wymaga odwołania polecenia właściciela, nie „dopisania linijki". */
            // Klucz + globalny wyłącznik modułu (dotąd „eteczka" wyłączona w widoczności menu i tak
            // zostawała w pasku bocznym).
            hasPermission("ModuleETeczka") && isModuleVisible("eteczka") && {
                type: "link", path: localizedPath("eTeczka"), icon: <FiFolder />, label: "E-Teczka", moduleCode: "eteczka",
            },
            // [SYGNALISTA] Konsola obsługi zgłoszeń naruszeń. Dotąd dostępna wyłącznie z panelu admina
            // albo z powiadomienia — członek działu obsługującego bez wejścia do /admin nie miał jak
            // do niej trafić. Klucz explicit-only, więc pozycja nie pojawia się nikomu „przy okazji".
            /* [polecenie właściciela 2026-09-22] „jak w access-control Sygnalista da się ukryć, to też
               «Obsługa Sygnalisty» powinna móc być wyłączona, NAWET JAK KTOŚ MA UPRAWNIENIA: widoczność
               z menu ukryta = moduł wyłączony, uprawnienia nie mają wpływu."
               Dlatego bramka widoczności stoi PRZED kluczem, a nie obok niego — klucz `SygnalistaManage`
               nie zapala pozycji w menu wyłączonego modułu. Dotyczy WYŁĄCZNIE paska pracownika:
               wejście z /admin i same końcówki API dalej działają na uprawnieniach (A15-06). */
            isModuleVisible("sygnalista") && hasPermission("SygnalistaManage") && {
                type: "link", path: localizedPath("hrSygnalista"), icon: <FiShield />, label: "Obsługa Sygnalisty",
            },
            // [brief 2026-09-01, pkt L13] Wnioski rejestracji: PRZEŁOŻONY (widzi wyłącznie wnioski
            // kierowane do swojego działu i jego poddrzewa — zakres liczy backend) oraz posiadacz
            // klucza AdminRegistration (widzi wszystko). Ranga konta nie jest już warunkiem klucza:
            // wcześniej „klucz + type >= 2" zamykał pozycję kadrowej bez rangi moderatora, choć to
            // ona te wnioski obsługuje. Sam klucz nadal nie zapala pozycji zwykłemu pracownikowi,
            // bo nie jest nadawany działowo (nadaje się go imiennie w panelu uprawnień).
            /* [polecenie właściciela 2026-09-06] POZYCJA ZAPALA SIĘ TĄ SAMĄ REGUŁĄ, KTÓRĄ SERWER LICZY ZAKRES.
               BYŁO źLE: `jobPosition >= 30 || type === 3` — STANOWISKO i RANGA, a dane idą po
               Department.HeadUserId. Rozjazd szedł w obie strony: dyrektor bez działu dostawał pozycję
               i pustą stronę, a kierownik działu ze stanowiskiem poniżej progu pozycji NIE MIAł, choć
               wnioski czekały właśnie na niego. `ModuleMojDzial` to klucz nadawany przy logowaniu za
               realne kierowanie działem — dokładnie ten warunek, o który chodzi. */
            /* [polecenie właściciela 2026-09-22] „jak są ukryte i nie ma tego okna, to z menu też znika
               «Wnioski rejestracji» — ALE UWAGA, w sekcji admin ZOSTAJĄ; z /admin nie ukrywamy żadnych
               zakładek. Widoczność menu odnosi się do menu pionowego dla pracowników."

               Dlatego bramka widoczności stoi TUTAJ — w pasku bocznym — i NIGDZIE INDZIEJ. Kafelek
               „Wnioski rejestracji" na landingu administracji kadr zostaje niezależnie od tego
               przełącznika; jego jedynym warunkiem jest klucz `AdminRegistration`.

               Kod `konfiguracjaKonta` to przełącznik FORMULARZA rejestracji (przeniesiony do
               /admin/access-control → Widoczność menu 2026-09-21). Świadomie nie zakładam drugiego,
               własnego kodu: skoro nie ma okna, którym ludzie składają wnioski, to kolejka tych
               wniosków też nie ma po co stać w menu pracownika. Jedno źródło, jeden przełącznik. */
            (hasPermission("ModuleMojDzial") || hasPermission("AdminRegistration"))
                && isModuleVisible("konfiguracjaKonta") && {
                type: "link", path: "/wnioski-rejestracji", icon: <FiUserPlus />, label: "Wnioski rejestracji",
            },
        ].filter(Boolean);
        if (obslugaItems.length > 0) {
            result.push({ title: "OBSŁUGA PRACOWNIKA", items: obslugaItems });
        }

        /* [P1 2026-10-01] ZGODNOŚĆ — wejścia redakcji z kluczem (opis przy KLUCZE_REDAKCJI_POLITYK). Każda pozycja: klucz ∧ widoczny moduł
           (ukryty moduł gasi pozycję MIMO klucza — reguła z 22.09); grupa bez pozycji się nie rysuje.
           • „Procedury i bezpieczeństwo” → /admin/procedury-bezpieczenstwo (jeden adres modułu polityk), gdy osoba ma KTÓRYKOLWIEK z pięciu
             kluczy Polityki*. Router /admin/:slug wpuszcza po kluczu kafelka, więc działa też bez rangi administratora. Na telefonie pozycji
             nie ma — /admin jest tam zamknięty (ta sama reguła co sekcja ADMINISTRACJA niżej). Liczba = moje procedury czekające na mój krok.
           • „Wykaz KSC” → /wykaz-ksc, wyłącznie z imiennym kluczem WykazKscPodglad (need-to-know; ranga nic nie otwiera). */
        const zgodnoscItems = [
            !isMobile && KLUCZE_REDAKCJI_POLITYK.some((k) => hasPermission(k)) && {
                type: "link", path: localizedPath("adminModule", { moduleSlug: SLUG_PROCEDUR }), icon: <FiArchive />, label: "Procedury i bezpieczeństwo", moduleCode: "polityki",
                licznik: wejsciaPolityk?.liczniki?.mojeDoDzialania, opisLicznika: (n) => `${odmienProcedury(n)} na Twój krok`,
            },
            hasPermission("WykazKscPodglad") && { type: "link", path: localizedPath("wykazKsc"), icon: <FiClock />, label: "Wykaz KSC", moduleCode: "wykazksc" },
        ].filter((item) => item && isModuleVisible(item.moduleCode));
        if (zgodnoscItems.length > 0) result.push({ title: "ZGODNOŚĆ", items: zgodnoscItems });

        const moduleItems = [
            { type: "link", path: localizedPath("contactForm"), icon: <FiMail />, label: "Formularz kontaktowy", moduleCode: "contactForm" },
            showJury && { type: "link", path: localizedPath("jury"), icon: <FiBarChart2 />, label: "Panel Jury" },
            { type: "link", path: localizedPath("sygnalista"), icon: <FiShield />, label: "Sygnalista", moduleCode: "sygnalista" },
        ].filter(item => item && (!item.moduleCode || isModuleVisible(item.moduleCode)));
        if (moduleItems.length > 0) result.push({ title: "MODUŁY", items: moduleItems });

        // [brief 2026-09-01, pkt L10] Panel administracyjny — także dla osoby BEZ rangi
        // administratora, o ile ma klucz choćby jednego kafelka. Właściciel: „jak ktoś nie ma
        // uprawnień do sekcji admina, to trzeba nadać — pojawi się przycisk Panel admina".
        // BYŁO ŹLE: przycisk zależał wyłącznie od rangi, więc nadanie klucza kafelka nie dawało
        // do niego ŻADNEJ drogi z menu — delegat musiał znać adres na pamięć. W środku i tak
        // widzi wyłącznie kafelki, do których ma klucz (AdminPanelPage filtruje po uprawnieniach).
        // [T2b 2026-09-14] Ten sam predykat co kafelki /admin i nagłówek (`czyPokazacPanelAdministracyjny` →
        // `czyKafelekDostepny`): klucz bramki trasy + widoczność modułu, a nie sam `tile.permission`.
        // [zgłoszenie właściciela 2026-09-16] „Zablokuj wejście do sekcji admin z urządzeń mobilnych."
        // Na telefonie grupy ADMINISTRACJA nie ma — trasa i tak jest zamknięta (ProtectedRoute), więc
        // pozostawiony odnośnik byłby wyłącznie drogą do ekranu odmowy.
        if (!isMobile && (user.type === 3 || czyPokazacPanelAdministracyjny({ hasPermission, isModuleRestricted, isModuleGloballyHidden, isAdmin }))) {
            result.push({
                title: "ADMINISTRACJA",
                items: [
                    {
                        type: "link", path: localizedPath("admin"), icon: <FiSettings />, label: "Panel administracyjny",
                        // Gaśnie tylko tam, gdzie świeci pozycja z ZGODNOŚCI — bez niej (moduł ukryty) panel zostaje jedynym podświetleniem.
                        nieAktywnyNa: zgodnoscItems.filter((i) => i.path.startsWith(`${localizedPath("admin")}/`)).map((i) => i.path),
                    },
                ],
            });
        }

        return result;
        /* `isAdmin` i `isModuleGloballyHidden` czyta predykat przycisku „Panel administracyjny"
           (`czyPokazacPanelAdministracyjny`) — brakowało ich na liście, więc pasek mógł zostać
           przy STARYM wyniku po zmianie widoczności modułów w tle. Obie wartości są stabilne
           (AuthContext: `isAdmin` = pochodna `user`, `isModuleGloballyHidden` w `useCallback`),
           więc dopisanie ich nie przelicza menu częściej niż dotąd. */
    }, [user, isMobile, hasPermission, isModuleRestricted, isModuleGloballyHidden, isAdmin, openNewTicketModal, isTicketCreateVisible, showJury, showRemoteReport, widocznoscModulow, maZasiegOperacyjny, trybZakladek, wybranyDzialMojegoDzialu, zakladkiWybranegoDzialu, wejsciaPolityk]);

    // Na mobile sidebar działa jako pełny drawer (zawsze z etykietami) — zwijanie do ikon
    // dotyczy WYŁĄCZNIE desktopu. Dzięki temu otwarty drawer nie pokazuje wąskiego paska ikon.
    const collapsed = isSidebarCollapsed && !isMobile;

    const sidebarClasses = [
        styles.sidebar,
        collapsed ? styles.collapsed : "",
        isSidebarOpen ? styles.mobileOpen : "",
    ].filter(Boolean).join(" ");

    return (
        <>
            <aside className={sidebarClasses}>
                <div className={styles.sidebarHeader}>
                    {!collapsed && (
                        <a href={localizedPath("dashboard")} className={styles.logoLink}>
                            <img src={logoUrl} alt={appName} className={styles.logo} />
                        </a>
                    )}
                    {isMobile ? (
                        <button className={styles.toggleBtn} onClick={closeMobileSidebar} title="Zamknij menu" aria-label="Zamknij menu">
                            <FiX />
                        </button>
                    ) : (
                        <button className={styles.toggleBtn} onClick={toggleSidebar} title={collapsed ? "Rozwiń menu" : "Zwiń menu"}>
                            {collapsed ? <FiChevronsRight /> : <FiChevronsLeft />}
                        </button>
                    )}
                </div>

                {/* [R40, właściciel 2026-09-13] „mój awatar w menu […] nie wszędzie jest widoczny”. Karta rysowała
                    WYŁĄCZNIE pierwszą literę imienia i nigdy nie pytała o zdjęcie (to nie była pozostałość maski
                    nagrania). Pudełko — rozmiar, zaokrąglenie, gradient, poświata — zostaje z arkusza menu; UserAvatar
                    wypełnia je zdjęciem z profilu albo z książki O365, a bez zdjęcia pokazuje ten sam inicjał co dotąd. */}
                {!collapsed && (
                    <div className={styles.userCard}>
                        <div className={styles.userAvatar}>
                            <UserAvatar
                                userId={user?.id}
                                firstName={user?.firstName}
                                lastName={user?.lastName}
                                wypelnij
                                dekoracyjny
                                inicjaly={<span>{userInitial}</span>}
                            />
                        </div>
                        <div className={styles.userMeta}>
                            <span className={styles.userCardName}>{user ? `${user.firstName} ${user.lastName}` : "..."}</span>
                            <span className={styles.userCardRole}>{user?.department || getRoleName(user?.type)}</span>
                        </div>
                    </div>
                )}
                {collapsed && (
                    <div className={styles.userCardCollapsed}>
                        <Tooltip label={user ? `${user.firstName} ${user.lastName}` : ""} show={true}>
                            <div className={styles.userAvatarSmall}>
                                <UserAvatar
                                    userId={user?.id}
                                    firstName={user?.firstName}
                                    lastName={user?.lastName}
                                    wypelnij
                                    dekoracyjny
                                    inicjaly={<span>{userInitial}</span>}
                                />
                            </div>
                        </Tooltip>
                    </div>
                )}

                <nav className={styles.nav}>
                    {sections.map((section) => (
                        <div key={section.title} className={styles.section}>
                            {!collapsed && <p className={styles.sectionTitle}>{section.title}</p>}
                            {collapsed && <div className={styles.sectionDivider} />}
                            <ul className={styles.sectionList}>
                                {section.items.map((item, i) => {
                                    if (item.type === "link") return <NavItem key={item.path || i} item={item} collapsed={collapsed} showDot={item.path && hasNew(item.path)} />;
                                    if (item.type === "dropdown") return <DropdownItem key={item.label || i} item={item} collapsed={collapsed} hasNewFn={hasNew} />;
                                    return null;
                                })}
                            </ul>
                        </div>
                    ))}
                </nav>

                <div className={styles.sidebarFooter}>
                    {/* Odnośniki społecznościowe jadą z BAZY (/admin/branding → BrandingContext).
                        Wcześniej cztery adresy były wpisane tutaj na sztywno, więc zmiana linku
                        wymagała przebudowy frontu. Pusta lista = brak ikon (komponent zwraca null). */}
                    {!collapsed && (
                        <SocialLinks className={styles.socialLinks} itemClassName={styles.socialLink} size={16} />
                    )}
                    <button onClick={logout} className={`${styles.logoutBtn} ${collapsed ? styles.logoutBtnCollapsed : ""}`}>
                        <FiLogOut />
                        {!collapsed && <span>Wyloguj się</span>}
                    </button>
                </div>
            </aside>

            {isSidebarOpen && isMobile && (
                <div className={styles.overlay} onClick={closeMobileSidebar} />
            )}
        </>
    );
};

export default Sidebar;
